import { useState, useEffect, useMemo, useCallback } from "react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import {
  Wifi,
  PlugZap,
  Activity,
  TrendingUp,
  DollarSign,
  BatteryCharging,
  RefreshCw,
  MapPin,
  ShieldCheck,
  HardDrive,
  Layers,
  BatteryMedium,
  Sun,
  Link2,
  Handshake,
  Wrench,
  Radio,
  Building2,
  ServerCrash,
  ChevronsUpDown,
} from "lucide-react";
import { useFleetData } from "../../lib/context/FleetDataProvider";
import { getSessionProfile } from "../../lib/utils/featureAccess";
import {
  CHART_COLORS,
  GAUGE_PRIMARY,
  GAUGE_TRACK,
  SEVERITY_COLORS,
  DATE_FORMAT_DD_MM_YYYY,
  CHART_REGISTRY,
  CHART_PICKER_STORAGE_KEY,
} from "./constants";
import { useFleetChartData } from "./hooks/useFleetChartData";
import HorizontalBarChart from "./charts/HorizontalBarChart";
import SemiCircleGauge from "./charts/SemiCircleGauge";
import SolidGauge from "./charts/SolidGauge";
import RadialRing from "./charts/RadialRing";
import FunnelChart from "./charts/FunnelChart";
import VerticalBarChart from "./charts/VerticalBarChart";
import BkpKpiCard from "../dashboard/bkp/components/KpiCards";
import useFleetUtilization from "./hooks/useFleetUtilization";
import useChargerLocations from "./hooks/useChargerLocations";
import TopSites from "../dashboard/bkp/components/TopSites";
import useTopSitesReducer from "../dashboard/bkp/components/TopSites/useTopSitesReducer";
import EnergySessionsChart from "../dashboard/bkp/components/EnergySessionsChart";
import StatusDonutChart from "../dashboard/bkp/components/StatusDonutChart";
import GeoMap from "./charts/GeoMap";
import ChartCard from "./ChartCard";
import ChartDataTable from "./ChartDataTable";
import CollapsibleSection from "./CollapsibleSection";
import ChartPicker from "./ChartPicker";
import DateRangeToolbar from "./DateRangeToolbar";
import { RANGE_TYPE_LABELS } from "../../lib/utils/constants";
import { getRange, isEnphaseUser } from "../../lib/utils/utility";
import "../dashboard/Dashboard.scss";
import "./FleetDashboard.scss";

export function FleetDashboard() {
  const navigate = useNavigate();
  const { state: fleetState } = useFleetData();
  const enphaseUser = isEnphaseUser();
  const [data, setData] = useState(() => (Array.isArray(fleetState?.data) ? fleetState.data : []));
  const hasGreeted = sessionStorage.getItem("fleet_greeted") === "true";
  const dataReady = !fleetState?.isLoading && Array.isArray(fleetState?.data);
  const [phase, setPhase] = useState(() => (hasGreeted || dataReady ? "done" : "loading"));
  const [activePreset, setActivePreset] = useState(RANGE_TYPE_LABELS.DAY_7);
  const [dateRange, setDateRange] = useState(() => getRange(RANGE_TYPE_LABELS.DAY_7));

  // Section collapse state (all collapsed by default)
  const SECTION_KEYS = ["operationalHealth", "siteLocations", "firmwareMaintenance", "fleetConfig", "deploymentConfig"];
  const [sectionOpen, setSectionOpen] = useState(() =>
    SECTION_KEYS.reduce((acc, k) => ({ ...acc, [k]: enphaseUser && k === "operationalHealth" ? true : false }), {})
  );
  const toggleSection = useCallback(key => {
    setSectionOpen(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);
  const allExpanded = SECTION_KEYS.every(k => sectionOpen[k]);
  const handleToggleAll = useCallback(() => {
    const next = !allExpanded;
    setSectionOpen(SECTION_KEYS.reduce((acc, k) => ({ ...acc, [k]: next }), {}));
  }, [allExpanded]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Chart visibility state (persisted in localStorage) ──
  const ALL_CHART_KEYS = useMemo(() => CHART_REGISTRY.map(c => c.key), []);
  const [visibleCharts, setVisibleCharts] = useState(() => {
    try {
      const stored = localStorage.getItem(CHART_PICKER_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch {
      /* ignore */
    }
    return ALL_CHART_KEYS; // default: all visible
  });

  const handleVisibleChartsChange = useCallback(next => {
    setVisibleCharts(next);
    localStorage.setItem(CHART_PICKER_STORAGE_KEY, JSON.stringify(next));
  }, []);

  const visibleSet = useMemo(() => new Set(visibleCharts), [visibleCharts]);
  const isVisible = useCallback(key => visibleSet.has(key), [visibleSet]);
  const hasVisibleCharts = useCallback(
    sectionKey => CHART_REGISTRY.some(c => c.section === sectionKey && visibleSet.has(c.key)),
    [visibleSet]
  );

  const handlePresetChange = useCallback(value => {
    setActivePreset(value);
    setDateRange(getRange(value));
  }, []);

  const handleCustomChange = useCallback((range, type) => {
    setDateRange(range);
    setActivePreset(type);
  }, []);

  /* Derive formatted date strings for utilization API */
  const startDate = useMemo(
    () => (dateRange?.[0] ? dayjs(dateRange[0]).format(DATE_FORMAT_DD_MM_YYYY) : ""),
    [dateRange]
  );
  const endDate = useMemo(
    () => (dateRange?.[1] ? dayjs(dateRange[1]).format(DATE_FORMAT_DD_MM_YYYY) : ""),
    [dateRange]
  );

  /* Fleet utilization: fetch per-site utilization and aggregate */
  const { data: utilizationData, isLoading: utilizationLoading } = useFleetUtilization(data, startDate, endDate);
  const energyData = utilizationData?.dailyData || [];

  /* Top sites by performance: derived from useFleetUtilization's per-site cache (no extra API calls) */
  const { state: topSitesState } = useTopSitesReducer(data, utilizationData?.perSite, utilizationLoading);
  const topSitesData = topSitesState?.data || [];

  /* Charger locations: fetch exact lat/lng only when Site Locations section is expanded */
  const { locations: siteLocations, isLoading: locationsLoading } = useChargerLocations(
    data,
    sectionOpen.siteLocations
  );

  const isLoading = fleetState?.isLoading;

  const userName = getSessionProfile().displayName;

  useEffect(() => {
    const incoming = Array.isArray(fleetState?.data) ? fleetState.data : [];
    setData(incoming);
  }, [fleetState?.data]);

  useEffect(() => {
    const fleetReady = !isLoading && Array.isArray(fleetState?.data);
    if (fleetReady && phase === "loading") {
      sessionStorage.setItem("fleet_greeted", "true");
      const fadeTimer = setTimeout(() => setPhase("fading"), 600);
      return () => clearTimeout(fadeTimer);
    }
    if (phase === "fading") {
      const doneTimer = setTimeout(() => setPhase("done"), 1600);
      return () => clearTimeout(doneTimer);
    }
  }, [isLoading, fleetState?.data, phase]);

  // ── Chart Data (extracted to custom hook per §4 — Separation of Concerns) ──
  const {
    connectivityData,
    faultedData,
    offPeakData,
    dlbData,
    partnerData,
    offGridData,
    modelData,
    fwData,
    phaseData,
    maxCurrentData,
    skuData,
    vendorData,
    commissioningData,
    modeData,
    fwFreshnessData,
    offlineDurationData,
    chargerStatusDonutData,
  } = useFleetChartData(data);

  // ── Drill-down: navigate to Fleet Search with pre-applied API filters ──
  const drillDown = useCallback(
    (apiFilterKey, entry) => {
      if (!entry?.name) return;

      // Map chart display names back to API-compatible filter values
      const BOOL_MAP = {
        // Connectivity
        Online: { isConnected: true },
        Offline: { isConnected: false },
        // Health
        Healthy: { isFaulted: false },
        Faulted: { isFaulted: true },
        // Off-Peak
        Eligible: { isOffPeakEligible: true },
        "Not Eligible": { isOffPeakEligible: false },
        // DLB
        "DLB Enabled": { dlbEnabled: true },
        "DLB Disabled": { dlbEnabled: false },
        // Partner
        "Partner Integrated": { hoControl: false },
        Standalone: { hoControl: true },
        // Off-Grid
        "Off-Grid": { isOffGrid: true },
        "On-Grid": { isOffGrid: false },
        // Commissioning
        Commissioned: { commissioningStatus: "1" },
        "Not Commissioned": { commissioningStatus: "0" },
        Pending: { commissioningStatus: "2" },
        // Mode
        Standard: { mode: "0" },
        Solar: { mode: "1" },
        "Off-Peak": { mode: "2" },
      };

      let filters;
      if (BOOL_MAP[entry.name]) {
        filters = BOOL_MAP[entry.name];
      } else if (apiFilterKey) {
        // For simple string-keyed charts (country, fwVersion, modelName, etc.)
        let value = entry.name;
        // Strip trailing "A" for maxCurrent chart values like "32A"
        if (apiFilterKey === "maxCurrent") value = value.replace(/A$/, "");
        // Strip "X-Phase" for phaseCount
        if (apiFilterKey === "phaseCount") value = value.replace(/-Phase$/, "");
        filters = { [apiFilterKey]: value };
      } else {
        return;
      }

      navigate("/fleet/list", { state: { drillDownFilters: filters } });
    },
    [navigate]
  );

  return (
    <div className="dashboard">
      {phase !== "done" && (
        <div className={`fleet-dashboard__splash ${phase === "fading" ? "fleet-dashboard__splash--fade" : ""}`}>
          <h1 className="fleet-dashboard__splash-text">Welcome back, {userName}</h1>
          <RefreshCw className="fleet-dashboard__spinner" size={32} />
          <p>Loading fleet data...</p>
        </div>
      )}
      <div
        className={`fleet-dashboard__content ${phase === "done" ? "fleet-dashboard__content--visible" : "fleet-dashboard__content--hidden"}`}
      >
        {/* ═══════════════════════════════════════════════
            ALWAYS-OPEN: Time Range + KPI + Energy
            ═══════════════════════════════════════════════ */}
        {!enphaseUser && (
          <div className="fleet-overview-panel">
            <DateRangeToolbar
              activePreset={activePreset}
              onPresetChange={handlePresetChange}
              onCustomChange={handleCustomChange}
            />
            <div className="kpi-grid kpi-grid--single-row">
              <BkpKpiCard
                label="Total Energy"
                value={utilizationData?.totalEnergyConsumedKwh || 0}
                unit="kWh"
                icon={Activity}
                iconClass="kpi-card__icon--orange"
                loading={utilizationLoading}
              />
              <BkpKpiCard
                label="Total Sessions"
                value={utilizationData?.totalSessions || 0}
                icon={BatteryCharging}
                iconClass="kpi-card__icon--blue"
                loading={utilizationLoading}
              />
              <BkpKpiCard
                label="Avg Utilization"
                value={utilizationData?.avgUtilizationPercent || 0}
                unit="%"
                icon={TrendingUp}
                iconClass="kpi-card__icon--green"
                loading={utilizationLoading}
              />
              <BkpKpiCard
                label="Total Cost"
                value={utilizationData?.totalSessionsCost || 0}
                prefix="$"
                icon={DollarSign}
                iconClass="kpi-card__icon--purple"
                loading={utilizationLoading}
              />
              <BkpKpiCard
                label="Total Utilization Time"
                value={utilizationData?.totalUtilizationTimeFormatted || "—"}
                icon={Activity}
                iconClass="kpi-card__icon--blue"
                loading={utilizationLoading}
              />
            </div>
            <div className="dashboard-charts dashboard-charts--full">
              {(energyData.length > 0 || utilizationLoading) && (
                <EnergySessionsChart data={energyData} loading={utilizationLoading} />
              )}
            </div>

            {/* ── Top Sites (always visible, not collapsible) ── */}
            <TopSites sites={topSitesData} loading={topSitesState?.isLoading} />
          </div>
        )}

        {/* ── Chart Picker + Expand All / Collapse All ── */}
        <div className="fleet-section-controls">
          <ChartPicker
            visibleCharts={visibleCharts}
            onChange={handleVisibleChartsChange}
            excludeSections={enphaseUser ? ["siteLocations"] : []}
          />
          <button type="button" className="fleet-section-controls__btn" onClick={handleToggleAll}>
            <ChevronsUpDown size={14} />
            {allExpanded ? "Collapse All" : "Expand All"}
          </button>
        </div>

        {/* ══════════════════════════════════════════════
            SECTION 1 — OPERATIONAL HEALTH
            ══════════════════════════════════════════════ */}
        {hasVisibleCharts("operationalHealth") && (
          <CollapsibleSection
            title="Operational Health"
            icon={Activity}
            isOpen={sectionOpen.operationalHealth}
            onToggle={() => toggleSection("operationalHealth")}
          >
            {(isVisible("chargerStatus") || isVisible("connectivity") || isVisible("fleetHealth")) && (
              <div className="dashboard-charts dashboard-charts--trio">
                {isVisible("chargerStatus") && chargerStatusDonutData.length > 0 && (
                  <StatusDonutChart data={chargerStatusDonutData} />
                )}
                {isVisible("connectivity") && (
                  <ChartCard
                    title="Connectivity"
                    icon={Wifi}
                    subtitle="Online vs Offline"
                    badge={`${connectivityData.find(d => d.name === "Online")?.value || 0} / ${(connectivityData.find(d => d.name === "Online")?.value || 0) + (connectivityData.find(d => d.name === "Offline")?.value || 0)} Online`}
                  >
                    <SemiCircleGauge
                      value={connectivityData.find(d => d.name === "Online")?.value || 0}
                      total={
                        (connectivityData.find(d => d.name === "Online")?.value || 0) +
                        (connectivityData.find(d => d.name === "Offline")?.value || 0)
                      }
                      color={GAUGE_PRIMARY}
                      trackColor={GAUGE_TRACK}
                    />
                    <ChartDataTable
                      data={connectivityData}
                      nameLabel="Status"
                      valueLabel="Chargers"
                      colors={[GAUGE_PRIMARY, GAUGE_TRACK]}
                    />
                  </ChartCard>
                )}
                {isVisible("fleetHealth") && (
                  <ChartCard
                    title="Fleet Health"
                    icon={ShieldCheck}
                    subtitle="Healthy vs Faulted"
                    badge={`${faultedData.find(d => d.name === "Healthy")?.value || 0} healthy · ${faultedData.find(d => d.name === "Faulted")?.value || 0} faulted`}
                  >
                    <SolidGauge
                      value={faultedData.find(d => d.name === "Healthy")?.value || 0}
                      max={
                        (faultedData.find(d => d.name === "Healthy")?.value || 0) +
                        (faultedData.find(d => d.name === "Faulted")?.value || 0)
                      }
                      color={GAUGE_PRIMARY}
                      trackColor={GAUGE_TRACK}
                    />
                    <ChartDataTable
                      data={faultedData}
                      nameLabel="Status"
                      valueLabel="Chargers"
                      colors={[GAUGE_PRIMARY, GAUGE_TRACK]}
                    />
                  </ChartCard>
                )}
              </div>
            )}

            {(isVisible("commissioning") || isVisible("offlineDuration")) && (
              <div className="dashboard-charts">
                {isVisible("commissioning") && (
                  <ChartCard
                    title="Commissioning Status"
                    icon={PlugZap}
                    subtitle="Deployment pipeline"
                    badge={`${commissioningData.reduce((s, d) => s + d.value, 0)} chargers`}
                  >
                    <FunnelChart
                      data={commissioningData}
                      colors={[CHART_COLORS[0], CHART_COLORS[6], CHART_COLORS[4]]}
                      onBarClick={e => drillDown(null, e)}
                    />
                    <ChartDataTable
                      data={commissioningData}
                      nameLabel="Status"
                      valueLabel="Chargers"
                      colors={[CHART_COLORS[0], CHART_COLORS[6], CHART_COLORS[4]]}
                    />
                  </ChartCard>
                )}
                {isVisible("offlineDuration") && (
                  <ChartCard
                    title="Offline Duration"
                    icon={ServerCrash}
                    subtitle="Downtime breakdown"
                    badge={`${data.filter(d => !d.isConnected).length} offline`}
                  >
                    <HorizontalBarChart data={offlineDurationData} colors={SEVERITY_COLORS} />
                    <ChartDataTable
                      data={offlineDurationData}
                      nameLabel="Duration"
                      valueLabel="Chargers"
                      colors={SEVERITY_COLORS}
                    />
                  </ChartCard>
                )}
              </div>
            )}
          </CollapsibleSection>
        )}

        {/* ══════════════════════════════════════════════
            SECTION 3 — SITE LOCATIONS & GEOGRAPHY
            ══════════════════════════════════════════════ */}
        {!enphaseUser && hasVisibleCharts("siteLocations") && (
          <CollapsibleSection
            title="Site Locations & Geography"
            icon={MapPin}
            isOpen={sectionOpen.siteLocations}
            onToggle={() => toggleSection("siteLocations")}
          >
            {isVisible("geoMap") && <GeoMap siteLocations={siteLocations} locationsLoading={locationsLoading} />}
          </CollapsibleSection>
        )}

        {/* ══════════════════════════════════════════════
            SECTION 4 — FIRMWARE & MAINTENANCE
            ══════════════════════════════════════════════ */}
        {hasVisibleCharts("firmwareMaintenance") && (
          <CollapsibleSection
            title="Firmware & Maintenance"
            icon={HardDrive}
            isOpen={sectionOpen.firmwareMaintenance}
            onToggle={() => toggleSection("firmwareMaintenance")}
          >
            {(isVisible("fwVersions") || isVisible("fwFreshness")) && (
              <div className="dashboard-charts">
                {isVisible("fwVersions") && (
                  <ChartCard
                    title="Firmware Versions"
                    icon={HardDrive}
                    subtitle="Fleet firmware"
                    badge={`${fwData.length} versions`}
                  >
                    <RadialRing
                      data={fwData}
                      colors={CHART_COLORS}
                      centerLabel="Firmware"
                      onSegmentClick={e => drillDown("fwVersion", e)}
                    />
                    <ChartDataTable data={fwData} nameLabel="Version" valueLabel="Chargers" colors={CHART_COLORS} />
                  </ChartCard>
                )}
                {isVisible("fwFreshness") && (
                  <ChartCard
                    title="Firmware Freshness"
                    icon={Wrench}
                    subtitle="Update recency"
                    badge={`${fwFreshnessData.reduce((s, d) => s + d.value, 0)} chargers`}
                  >
                    <VerticalBarChart
                      data={fwFreshnessData}
                      colors={SEVERITY_COLORS}
                      onBarClick={e => drillDown(null, e)}
                    />
                    <ChartDataTable
                      data={fwFreshnessData}
                      nameLabel="Freshness"
                      valueLabel="Chargers"
                      colors={SEVERITY_COLORS}
                    />
                  </ChartCard>
                )}
              </div>
            )}

            {(isVisible("chargerModel") || isVisible("skuDistribution")) && (
              <div className="dashboard-charts">
                {isVisible("chargerModel") && (
                  <ChartCard
                    title="Chargers by Model"
                    icon={PlugZap}
                    subtitle="Model breakdown"
                    badge={`${modelData.length} models`}
                  >
                    <HorizontalBarChart
                      data={modelData}
                      colors={CHART_COLORS}
                      onBarClick={e => drillDown("modelName", e)}
                    />
                    <ChartDataTable data={modelData} nameLabel="Model" valueLabel="Chargers" colors={CHART_COLORS} />
                  </ChartCard>
                )}
                {isVisible("skuDistribution") && (
                  <ChartCard
                    title="SKU Distribution"
                    icon={Radio}
                    subtitle="SKU breakdown"
                    badge={`${skuData.length} SKUs`}
                  >
                    <HorizontalBarChart data={skuData} colors={CHART_COLORS} onBarClick={e => drillDown("sku", e)} />
                    <ChartDataTable data={skuData} nameLabel="SKU" valueLabel="Chargers" colors={CHART_COLORS} />
                  </ChartCard>
                )}
              </div>
            )}
          </CollapsibleSection>
        )}

        {/* ══════════════════════════════════════════════
            SECTION 5 — FLEET CONFIGURATION
            ══════════════════════════════════════════════ */}
        {hasVisibleCharts("fleetConfig") && (
          <CollapsibleSection
            title="Fleet Configuration"
            icon={Layers}
            isOpen={sectionOpen.fleetConfig}
            onToggle={() => toggleSection("fleetConfig")}
          >
            {(isVisible("phaseConfig") || isVisible("dlbAdoption")) && (
              <div className="dashboard-charts">
                {isVisible("phaseConfig") && (
                  <ChartCard
                    title="Phase Configuration"
                    icon={Layers}
                    subtitle="Phase Distribution"
                    badge={`${phaseData.length} types`}
                  >
                    <RadialRing
                      data={phaseData}
                      colors={CHART_COLORS}
                      centerLabel="Phase"
                      onSegmentClick={e => drillDown("phaseCount", e)}
                    />
                    <ChartDataTable data={phaseData} nameLabel="Phase" valueLabel="Chargers" colors={CHART_COLORS} />
                  </ChartCard>
                )}
                {isVisible("dlbAdoption") && (
                  <ChartCard
                    title="DLB Adoption"
                    icon={Link2}
                    subtitle="Load balancing"
                    badge={`${dlbData.reduce((s, d) => s + d.value, 0)} chargers`}
                  >
                    <RadialRing
                      data={dlbData}
                      colors={[CHART_COLORS[0], CHART_COLORS[6]]}
                      centerLabel="DLB"
                      onSegmentClick={e => drillDown(null, e)}
                    />
                    <ChartDataTable
                      data={dlbData}
                      nameLabel="Status"
                      valueLabel="Chargers"
                      colors={[CHART_COLORS[0], CHART_COLORS[6]]}
                    />
                  </ChartCard>
                )}
              </div>
            )}

            {(isVisible("partnerIntegration") || isVisible("offGridStatus")) && (
              <div className="dashboard-charts">
                {isVisible("partnerIntegration") && (
                  <ChartCard
                    title="Partner Integration"
                    icon={Handshake}
                    subtitle="Integration coverage"
                    badge={`${partnerData.length} groups`}
                  >
                    <RadialRing
                      data={partnerData}
                      colors={[CHART_COLORS[1], CHART_COLORS[6]]}
                      centerLabel="Partners"
                      onSegmentClick={e => drillDown(null, e)}
                    />
                    <ChartDataTable
                      data={partnerData}
                      nameLabel="Type"
                      valueLabel="Chargers"
                      colors={[CHART_COLORS[1], CHART_COLORS[6]]}
                    />
                  </ChartCard>
                )}
                {isVisible("offGridStatus") && (
                  <ChartCard
                    title="Off-Grid Status"
                    icon={Sun}
                    subtitle="Grid connectivity"
                    badge={`${offGridData.reduce((s, d) => s + d.value, 0)} chargers`}
                  >
                    <RadialRing
                      data={offGridData}
                      colors={[CHART_COLORS[4], CHART_COLORS[0]]}
                      centerLabel="Grid"
                      onSegmentClick={e => drillDown(null, e)}
                    />
                    <ChartDataTable
                      data={offGridData}
                      nameLabel="Status"
                      valueLabel="Chargers"
                      colors={[CHART_COLORS[4], CHART_COLORS[0]]}
                    />
                  </ChartCard>
                )}
              </div>
            )}
          </CollapsibleSection>
        )}

        {/* ══════════════════════════════════════════════
            SECTION 5 — DEPLOYMENT & CONFIG
            ══════════════════════════════════════════════ */}
        {hasVisibleCharts("deploymentConfig") && (
          <CollapsibleSection
            title="Deployment & Config"
            icon={BatteryMedium}
            isOpen={sectionOpen.deploymentConfig}
            onToggle={() => toggleSection("deploymentConfig")}
          >
            {(isVisible("chargingMode") || isVisible("offPeakEligibility")) && (
              <div className="dashboard-charts">
                {isVisible("chargingMode") && (
                  <ChartCard
                    title="Charging Mode"
                    icon={BatteryMedium}
                    subtitle="Mode distribution"
                    badge={`${modeData.length} modes`}
                  >
                    <FunnelChart
                      data={modeData}
                      colors={[CHART_COLORS[1], CHART_COLORS[4], CHART_COLORS[2]]}
                      onBarClick={e => drillDown(null, e)}
                    />
                    <ChartDataTable
                      data={modeData}
                      nameLabel="Mode"
                      valueLabel="Chargers"
                      colors={[CHART_COLORS[1], CHART_COLORS[4], CHART_COLORS[2]]}
                    />
                  </ChartCard>
                )}
                {isVisible("offPeakEligibility") && (
                  <ChartCard
                    title="Off-Peak Eligibility"
                    icon={Sun}
                    subtitle="Fleet eligibility"
                    badge={`${(offPeakData.find(d => d.name === "Eligible")?.value || 0) + (offPeakData.find(d => d.name === "Not Eligible")?.value || 0)} chargers`}
                  >
                    <SolidGauge
                      value={offPeakData.find(d => d.name === "Eligible")?.value || 0}
                      max={
                        (offPeakData.find(d => d.name === "Eligible")?.value || 0) +
                        (offPeakData.find(d => d.name === "Not Eligible")?.value || 0)
                      }
                      label="Eligible Chargers"
                      color={GAUGE_PRIMARY}
                      trackColor={GAUGE_TRACK}
                    />
                    <ChartDataTable
                      data={offPeakData}
                      nameLabel="Eligibility"
                      valueLabel="Chargers"
                      colors={[GAUGE_PRIMARY, GAUGE_TRACK]}
                    />
                  </ChartCard>
                )}
              </div>
            )}

            {(isVisible("vendorDistribution") || isVisible("maxCurrent")) && (
              <div className="dashboard-charts">
                {isVisible("vendorDistribution") && (
                  <ChartCard
                    title="Vendor Distribution"
                    icon={Building2}
                    subtitle="Manufacturer split"
                    badge={`${vendorData.length} vendors`}
                  >
                    <HorizontalBarChart
                      data={vendorData}
                      colors={CHART_COLORS}
                      onBarClick={e => drillDown("vendorName", e)}
                    />
                    <ChartDataTable data={vendorData} nameLabel="Vendor" valueLabel="Chargers" colors={CHART_COLORS} />
                  </ChartCard>
                )}
                {isVisible("maxCurrent") && (
                  <ChartCard
                    title="Max Current Distribution"
                    icon={PlugZap}
                    subtitle="Fleet power ratings"
                    badge={`${maxCurrentData.length} ratings`}
                  >
                    <HorizontalBarChart
                      data={maxCurrentData}
                      colors={CHART_COLORS}
                      onBarClick={e => drillDown("maxCurrent", e)}
                    />
                    <ChartDataTable
                      data={maxCurrentData}
                      nameLabel="Rating"
                      valueLabel="Chargers"
                      colors={CHART_COLORS}
                    />
                  </ChartCard>
                )}
              </div>
            )}
          </CollapsibleSection>
        )}
      </div>
    </div>
  );
}

export default FleetDashboard;
